import { z } from "zod";

const note = z.object({
  id: z.string(),
  kind: z.enum(["note", "requirement"]),
  text: z.string(),
});
const primitive = z.enum([
  "string",
  "number",
  "boolean",
  "date",
  "uuid",
  "json",
]);
const port = z.object({
  id: z.string(),
  name: z.string(),
  format: z.enum(["primitive", "list", "object"]),
  primitiveType: primitive.optional(),
  required: z.boolean(),
  direction: z.enum(["input", "output"]),
  description: z.string().optional(),
  notes: z.array(note).default([]),
  dbField: z.object({ tableId: z.string(), columnId: z.string() }).optional(),
});

export const flowDocumentSchema = z
  .object({
    version: z.literal(2).optional(),
    nodes: z
      .array(
        z.object({
          id: z.string().min(1),
          type: z.literal("ideaNode").default("ideaNode"),
          position: z.object({ x: z.number(), y: z.number() }),
          data: z.object({
            nodeKind: z.enum(["action", "signal", "state"]),
            title: z.string(),
            description: z.string(),
            notes: z.array(note).default([]),
            ports: z.array(port).default([]),
            executionGate: z.boolean().optional(),
            actionPayload: z
              .object({
                subtype: z.enum([
                  "local_function",
                  "external_api",
                  "llm_prompt",
                ]),
                functionSignature: z.string().optional(),
                httpMethod: z
                  .enum(["GET", "POST", "PUT", "PATCH", "DELETE"])
                  .optional(),
                endpoint: z.string().optional(),
                systemPrompt: z.string().optional(),
                userTemplate: z.string().optional(),
              })
              .optional(),
            signal: z
              .object({
                event: z.string(),
                source: z.string(),
                payload: z.string(),
              })
              .optional(),
            state: z
              .object({
                value: z.string(),
                enterWhen: z.string(),
                exitWhen: z.string(),
              })
              .optional(),
            testInput: z.string().optional(),
            expectedOutput: z.string().optional(),
            runs: z
              .array(
                z.object({
                  id: z.string(),
                  at: z.string(),
                  input: z.string(),
                  systemPrompt: z.string(),
                  prompt: z.string(),
                  output: z.string(),
                  status: z.enum(["success", "error", "cancelled"]),
                  durationMs: z.number(),
                  model: z.string().optional(),
                  error: z.string().optional(),
                }),
              )
              .max(5)
              .optional(),
          }),
        }),
      )
      .max(500),
    edges: z
      .array(
        z.object({
          id: z.string(),
          source: z.string(),
          target: z.string(),
          sourceHandle: z.string().nullable().optional(),
          targetHandle: z.string().nullable().optional(),
          type: z.string().optional(),
          animated: z.boolean().optional(),
        }),
      )
      .max(2000),
    dbSchema: z
      .object({
        tables: z.array(
          z.object({
            id: z.string(),
            name: z.string(),
            columns: z.array(
              z.object({
                id: z.string(),
                name: z.string(),
                type: primitive,
                nullable: z.boolean(),
                description: z.string().optional(),
              }),
            ),
          }),
        ),
      })
      .optional(),
  })
  .superRefine((doc, ctx) => {
    const ids = new Set(doc.nodes.map((n) => n.id));
    if (
      ids.size !== doc.nodes.length ||
      new Set(doc.edges.map((e) => e.id)).size !== doc.edges.length
    )
      ctx.addIssue({ code: "custom", message: "IDs müssen eindeutig sein." });
    for (const edge of doc.edges) {
      if (!ids.has(edge.source) || !ids.has(edge.target))
        ctx.addIssue({
          code: "custom",
          message: "Verbindung verweist auf fehlenden Schritt.",
        });
    }
  });
